import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ArrowLeft, Check, Crown, Award, Shield, Loader2, CreditCard, AlertCircle, Lock } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#ffffff',
      fontFamily: '"Inter", sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': { color: '#71717a' },
      backgroundColor: 'transparent',
      iconColor: '#E17431',
    },
    invalid: {
      color: '#ef4444',
      iconColor: '#ef4444',
    },
  },
};

const getAuthHeader = () => {
  const user = localStorage.getItem('user');
  if (user) {
    const userData = JSON.parse(user);
    if (userData.token) {
      return { Authorization: `Bearer ${userData.token}` };
    }
  }
  return {};
};

const DriverMembership = () => {
  const navigate = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [currentMembership, setCurrentMembership] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch tiers with auth header
      const tiersResponse = await axios.get(`${API}/membership/tiers`, {
        headers: getAuthHeader()
      });
      setTiers(tiersResponse.data.tiers);

      // Fetch current membership
      const currentResponse = await axios.get(`${API}/membership/current`, {
        headers: getAuthHeader()
      });
      setCurrentMembership(currentResponse.data);
    } catch (error) {
      console.error('Error fetching membership data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load membership information',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (tierId) => {
    // Show payment modal first
    setSelectedTier(tierId);
    setShowPaymentModal(true);
  };

  const submitSubscription = async (paymentMethodId) => {
    setSubscribing(selectedTier);
    try {
      const response = await axios.post(
        `${API}/membership/subscribe`,
        {
          tier: selectedTier,
          payment_method_id: paymentMethodId
        },
        { headers: getAuthHeader() }
      );

      toast({
        title: 'Success!',
        description: response.data.message,
        duration: 7000
      });

      setShowPaymentModal(false);
      // Refresh membership data
      fetchData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to subscribe',
        variant: 'destructive'
      });
    } finally {
      setSubscribing(null);
    }
  };

  const handleUpgrade = async (tierId) => {
    setSubscribing(tierId);
    try {
      const response = await axios.put(
        `${API}/membership/upgrade`,
        { tier: tierId },
        { headers: getAuthHeader() }
      );

      toast({
        title: 'Success!',
        description: response.data.message
      });

      fetchData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to upgrade',
        variant: 'destructive'
      });
    } finally {
      setSubscribing(null);
    }
  };

  const getTierIcon = (tierId) => {
    switch (tierId) {
      case 'gold':
        return <Crown className="w-8 h-8 text-yellow-500" />;
      case 'silver':
        return <Award className="w-8 h-8 text-gray-400" />;
      case 'bronze':
        return <Shield className="w-8 h-8 text-wheely-orange-dark" />;
      default:
        return null;
    }
  };

  const getTierColor = (tierId) => {
    switch (tierId) {
      case 'gold':
        return 'from-yellow-500 to-yellow-600';
      case 'silver':
        return 'from-gray-400 to-gray-500';
      case 'bronze':
        return 'from-wheely-orange-dark to-orange-700';
      default:
        return 'from-wheely-orange to-wheely-orange-dark';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-wheely-navy-950 flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-wheely-orange border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-wheely-navy-950">
      {/* Header */}
      <div className="bg-gradient-to-r from-wheely-navy-950 to-wheely-navy-900 border-b border-wheely-orange/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/driver/dashboard')}
            className="text-wheely-orange hover:text-wheely-orange-light hover:bg-wheely-orange/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-white mb-4">Driver Membership Plans</h2>
          <p className="text-xl text-gray-400">Choose the plan that fits your business</p>
          {currentMembership?.has_membership && (
            <div className="mt-4 space-y-2">
              <Badge className="bg-green-500 text-white text-lg px-4 py-2">
                Current: {currentMembership.name} Member
              </Badge>
              {currentMembership.is_trial && (
                <div className="mt-2">
                  <Badge className="bg-wheely-orange text-white text-lg px-4 py-2">
                    🎉 FREE TRIAL - {currentMembership.trial_days_remaining} days remaining
                  </Badge>
                  <p className="text-gray-400 mt-2 text-sm">
                    Upgrade now to save on your first month with pro-rated discount!
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Membership Tiers */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {tiers.map((tier) => {
            const isCurrentTier = currentMembership?.tier === tier.id;
            const canUpgrade = currentMembership?.has_membership && !isCurrentTier;
            const upgradeDiscount = tier.upgrade_discount;
            const isBronze = tier.id === 'bronze';
            
            return (
              <Card
                key={tier.id}
                className={`bg-gradient-to-br from-wheely-navy-950 to-wheely-navy-900 border-2 ${
                  isCurrentTier ? 'border-wheely-orange' : 'border-wheely-orange/20'
                } hover:border-wheely-orange/50 transition-all duration-300 ${
                  tier.id === 'gold' ? 'transform scale-105' : ''
                }`}
              >
                <CardHeader>
                  <div className="flex justify-center mb-4">
                    <div className={`bg-gradient-to-br ${getTierColor(tier.id)} p-4 rounded-2xl`}>
                      {getTierIcon(tier.id)}
                    </div>
                  </div>
                  {isBronze && (
                    <Badge className="mb-2 bg-green-500 text-white mx-auto">
                      3 MONTHS FREE
                    </Badge>
                  )}
                  <CardTitle className="text-white text-3xl text-center">{tier.name}</CardTitle>
                  <CardDescription className="text-gray-400 text-center">{tier.description}</CardDescription>
                  <div className="text-center mt-4">
                    {upgradeDiscount && upgradeDiscount.discount > 0 ? (
                      <div>
                        <div className="text-gray-400 line-through text-2xl">£{tier.price}</div>
                        <div className="text-5xl font-bold text-green-500">
                          £{upgradeDiscount.first_month_price.toFixed(2)}
                          <span className="text-xl text-gray-400">/mo</span>
                        </div>
                        <p className="text-wheely-orange font-semibold mt-2">
                          Save £{upgradeDiscount.savings.toFixed(2)} on first month!
                        </p>
                        <p className="text-gray-500 text-xs mt-1">
                          ({upgradeDiscount.remaining_months} months trial credit)
                        </p>
                      </div>
                    ) : (
                      <div>
                        <div className="text-5xl font-bold text-white">
                          {isBronze ? 'FREE' : `£${tier.price}`}
                          {!isBronze && <span className="text-xl text-gray-400">/mo</span>}
                        </div>
                        {isBronze && (
                          <p className="text-gray-400 text-sm mt-2">for 90 days</p>
                        )}
                      </div>
                    )}
                    <p className="text-wheely-orange font-semibold mt-2">{parseFloat(tier.commission_rate).toFixed(1)}% Commission</p>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    {tier.features.map((feature, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <div className="pt-4">
                    {isCurrentTier ? (
                      <Button
                        disabled
                        className="w-full bg-green-600 text-white"
                      >
                        <Check className="w-4 h-4 mr-2" />
                        Current Plan
                      </Button>
                    ) : (
                      <Button
                        onClick={() => canUpgrade ? handleUpgrade(tier.id) : handleSubscribe(tier.id)}
                        disabled={subscribing === tier.id}
                        className={`w-full bg-gradient-to-r ${getTierColor(tier.id)} hover:opacity-90 text-white font-semibold`}
                      >
                        {subscribing === tier.id ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Processing...
                          </>
                        ) : (
                          canUpgrade ? 'Upgrade' : 'Subscribe'
                        )}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Benefits Section */}
        {currentMembership?.is_trial && (
          <Card className="bg-gradient-to-r from-wheely-orange to-wheely-orange-dark border-0 mb-8">
            <CardContent className="py-8">
              <h2 className="text-white text-3xl font-bold text-center mb-4">
                💰 Upgrade Now & Save Money!
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-white">
                <div className="bg-white/10 rounded-lg p-6">
                  <h3 className="font-bold text-xl mb-3">🚀 Stop Wasting Money on Commission</h3>
                  <p className="text-orange-100">
                    Upgrade to <strong>Gold</strong> today and drop your commission from <strong>5%</strong> to <strong>2.5%</strong>. 
                    This saving alone covers the membership fee, PLUS you get your remaining free trial credited as an extra discount!
                  </p>
                  <p className="mt-3 font-semibold text-yellow-200">
                    With {currentMembership.trial_days_remaining} days left = £{((currentMembership.trial_days_remaining / 30) * 29.99).toFixed(2)} credit!
                  </p>
                </div>
                <div className="bg-white/10 rounded-lg p-6">
                  <h3 className="font-bold text-xl mb-3">⚡ Immediate Access to Better Leads</h3>
                  <p className="text-orange-100">
                    Don't wait {currentMembership.trial_days_remaining} days to see the best leads. 
                    Upgrade now, get your discount, and jump straight into the <strong>priority dispatch queue</strong>.
                  </p>
                  <p className="mt-3 font-semibold text-yellow-200">
                    Gold members get first access to high-value jobs!
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="bg-gradient-to-br from-wheely-navy-950 to-wheely-navy-900 border-wheely-orange/20">
          <CardHeader>
            <CardTitle className="text-white text-2xl">Why Become a Member?</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="bg-wheely-orange/10 p-3 rounded-lg">
                  <Check className="w-6 h-6 text-wheely-orange" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Lower Commission Rates</h3>
                  <p className="text-gray-400 text-sm">Keep more of what you earn with reduced commission fees</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="bg-wheely-orange/10 p-3 rounded-lg">
                  <Check className="w-6 h-6 text-wheely-orange" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Priority Requests</h3>
                  <p className="text-gray-400 text-sm">Gold members get first access to high-value jobs</p>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="bg-wheely-orange/10 p-3 rounded-lg">
                  <Check className="w-6 h-6 text-wheely-orange" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Better Lead Quality</h3>
                  <p className="text-gray-400 text-sm">Higher tiers get access to premium customers</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="bg-wheely-orange/10 p-3 rounded-lg">
                  <Check className="w-6 h-6 text-wheely-orange" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Premium Support</h3>
                  <p className="text-gray-400 text-sm">Get faster response times and dedicated support</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="bg-gradient-to-br from-wheely-navy-950 to-wheely-navy-900 border-wheely-orange/20 text-white">
          <DialogHeader>
            <DialogTitle className="text-2xl flex items-center gap-2">
              <CreditCard className="w-6 h-6 text-wheely-orange" />
              Payment Details Required
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {selectedTier === 'bronze' ? (
                <div className="bg-wheely-orange/10 border border-wheely-orange/30 rounded-lg p-3 mt-2 flex gap-2">
                  <AlertCircle className="w-5 h-5 text-wheely-orange flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-orange-300 font-semibold">3-Month Free Trial - Card Required</p>
                    <p className="text-sm text-gray-300 mt-1">
                      Your card will be charged £29.99/month starting on day 91. Cancel anytime during the trial period.
                    </p>
                  </div>
                </div>
              ) : (
                <p className="mt-2">Enter your card details to start your {selectedTier} membership.</p>
              )}
            </DialogDescription>
          </DialogHeader>

          <Elements stripe={stripePromise} options={{ locale: 'en-GB' }}>
            <MembershipPaymentForm
              selectedTier={selectedTier}
              subscribing={subscribing === selectedTier}
              onSubmit={submitSubscription}
              onCancel={() => setShowPaymentModal(false)}
            />
          </Elements>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const MembershipPaymentForm = ({ selectedTier, subscribing, onSubmit, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [cardError, setCardError] = useState(null);

  const handleSubmit = async () => {
    if (!stripe || !elements) return;
    setCardError(null);

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: elements.getElement(CardElement)
    });

    if (error) {
      setCardError(error.message);
      return;
    }

    onSubmit(paymentMethod.id);
  };

  return (
    <div className="space-y-4 mt-4">
      <div className="space-y-2">
        <Label className="text-white text-sm font-medium flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-wheely-orange-light" /> Card Details
        </Label>
        <div className="bg-wheely-navy-800 border border-wheely-navy-700 rounded-lg px-4 py-4 focus-within:border-wheely-orange transition-colors">
          <CardElement options={CARD_ELEMENT_OPTIONS} onChange={() => setCardError(null)} />
        </div>
        {cardError && (
          <p className="text-red-400 text-sm flex items-center gap-1">
            <span>⚠</span> {cardError}
          </p>
        )}
        <p className="text-zinc-500 text-xs">Test card: 4242 4242 4242 4242 · Any future date · Any CVC</p>
      </div>

      <div className="flex gap-2 pt-4">
        <Button
          onClick={handleSubmit}
          disabled={subscribing || !stripe}
          className="flex-1 bg-gradient-to-r from-wheely-orange to-wheely-orange-dark hover:from-wheely-orange-dark hover:to-orange-700 text-white"
        >
          {subscribing ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Processing...
            </>
          ) : selectedTier === 'bronze' ? (
            'Start Free Trial'
          ) : (
            'Subscribe Now'
          )}
        </Button>
        <Button
          variant="outline"
          onClick={onCancel}
          className="border-wheely-navy-700 text-gray-300"
        >
          Cancel
        </Button>
      </div>
      <p className="text-center text-zinc-500 text-xs flex items-center justify-center gap-1">
        <Lock className="w-3 h-3" /> Secured by Stripe · 256-bit encryption
      </p>
    </div>
  );
};

export default DriverMembership;
